import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Eye,
  Trash2,
  Hash,
  CheckCircle2,
  XCircle,
  User,
  Phone,
  Mail,
  MapPin,
  Building,
  Package,
  Calendar,
  CreditCard,
  ShoppingCart,
  RotateCcw,
} from "lucide-react";
import {
  Modal,
  SearchBar,
} from "@/components/common";
import {
  FormInput,
  FormSelect,
  FormTextarea,
} from "@/components/ui";
import { fmt } from "@/lib/formatters";
import toast from "react-hot-toast";
import * as services from "./services";
import { useAuthStore } from "@/store/authStore";
import { PurchaseForm } from "../../components/PurchaseForm";

const Table = ({ headers, children }) => (
  <div className="w-full rounded-lg border border-slate-200 overflow-hidden bg-white">
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead className="text-xs text-slate-500 bg-slate-50 border-b border-slate-200">
          <tr>{headers.map((h, i) => <th key={i} className="px-4 py-3 font-semibold text-slate-600">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  </div>
);

// Helper components for the premium View Modal
const DetailField = ({ label, value, fullWidth = false, className = "" }) => (
  <div
    className={`${fullWidth ? "col-span-full" : "col-span-1"} bg-slate-50 rounded-lg p-3 transition-all duration-300`}
  >
    <div className="text-[12px] text-slate-500 mb-0.5  font-semibold tracking-wider opacity-70">
      {label}
    </div>
    <div className={`text-xs font-semibold text-brand-dark ${className}`}>
      {value || "—"}
    </div>
  </div>
);

export const InventorySettings = () => {
  const [activeTab, setActiveTab] = useState("category"); // values: category, unit, supplier, brand
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [loading, setLoading] = useState(false);

  // Data from Backend
  const [data, setData] = useState([]);
  const [gstRegisteredTypes, setGstRegisteredTypes] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [stores, setStores] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);
  const [errors, setErrors] = useState({});
  const [isPurchaseFormOpen, setIsPurchaseFormOpen] = useState(false);
  const { user } = useAuthStore();

  const fetchSettings = async () => {
    setLoading(true);
    try {
      let res;
      if (activeTab === "purchase") {
        res = await services.getPurchases();
      } else {
        const mappedTab = activeTab === "supplier" ? "vendor" : activeTab;
        res = await services.getInventorySettings({ settingType: mappedTab });
      }

      if (res.status === "success") {
        setData(res.data);
      }
    } catch (err) {
      toast.error("Failed to fetch settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      // Fetch GST registered types
      const gstRes = await services.getInventorySettings({
        settingType: "registeredType",
      });
      if (gstRes.status === "success") {
        const gstOptions = gstRes.data.map((g) => {
          const gTitle = g.registeredTypeName || g.title || "";
          const gCode = g.registeredTypeCode || g.code || "";
          return {
            ...g,
            label: gCode ? `${gTitle} (${gCode})` : gTitle || "—",
          };
        });
        setGstRegisteredTypes(gstOptions);
      }

      // Fetch vendors for dropdown
      const vendorRes = await services.getInventorySettings({
        settingType: "vendor",
      });
      if (vendorRes.status === "success") {
        const vendorOptions = vendorRes.data.map((v) => {
          const vTitle = v.vendorName || v.name || v.title || "";
          const vCode = v.vendorCode || v.code || "";
          return {
            ...v,
            label: vCode ? `${vTitle} (${vCode})` : vTitle || "—",
          };
        });
        setVendors(vendorOptions);
      }

      // Fetch categories for item form
      const categoryRes = await services.getInventorySettings({
        settingType: "category",
      });
      if (categoryRes.status === "success") {
        const categoryOptions = categoryRes.data.map((c) => {
          const cTitle = c.categoryName || c.title || "";
          const cCode = c.categoryCode || c.code || "";
          return {
            ...c,
            label: cCode ? `${cTitle} (${cCode})` : cTitle || "—",
          };
        });
        setCategories(categoryOptions);
      }

      // Fetch brands for item form
      const brandRes = await services.getInventorySettings({
        settingType: "brand",
      });
      if (brandRes.status === "success") {
        const brandOptions = brandRes.data.map((b) => {
          const bTitle = b.brandName || b.title || "";
          const bCode = b.brandCode || b.code || "";
          return {
            ...b,
            label: bCode ? `${bTitle} (${bCode})` : bTitle || "—",
          };
        });
        setBrands(brandOptions);
      }

      // Fetch units for item form
      const unitRes = await services.getInventorySettings({
        settingType: "unit",
      });
      if (unitRes.status === "success") {
        const unitOptions = unitRes.data.map((u) => {
          const uTitle = u.unitName || u.title || "";
          const uCode = u.unitCode || u.code || "";
          return {
            ...u,
            label: uCode ? `${uTitle} (${uCode})` : uTitle || "—",
          };
        });
        setUnits(unitOptions);
      }

      // Fetch stores for purchase form
      const storeRes = await services.getInventorySettings({
        settingType: "stores",
      });
      if (storeRes.status === "success") {
        setStores(storeRes.data);
      }
    } catch (err) {
      console.error("Failed to fetch dropdown data:", err);
      toast.error("Failed to load dropdown lists (Categories, Brands, etc.)");
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (!isModalOpen) {
      fetchSettings();
    } else {
      // Refresh dropdown data when modal opens
      fetchDropdownData();
    }
  }, [activeTab, isModalOpen]);

  const TABS = [
    { id: "supplier", label: "Vendors" },
    { id: "brand", label: "Brands" },
    { id: "stores", label: "Stores" },
    { id: "category", label: "Categories" },
    { id: "item", label: "Items" },
    { id: "unit", label: "Units" },
    { id: "registeredType", label: "Registered Types" },
    // { id: "purchase", label: "Purchases" },
  ];

    const [form, setForm] = useState({
    title: "",
    code: "",
    description: "",
    name: "",
    mobile: "",
    email: "",
    address: "",
    serviceType: "Verve",
    active: true,
    openingDate: "",
    state: "",
    district: "",
    gstRegisteredType: "",
    gstNo: "",
    itemName: "",
    type: "",
    category: "",
    brand: "",
    itemCode: "",
    hsnSacCode: "",
    isUnique: false,
    uniqueName: "",
    isBatchExpiry: false,
    barcode: "",
    units: "",
    itemDescription: "",
    bankIfsc: "",
    bankName: "",
    bankBranch: "",
    bankAccountHolder: "",
    bankAccountNo: "",
    buyer: "",
    store: "",
    vendor: "",
    date: "",
    billNo: "",
  });

  const handleAdd = () => {
    setEditingItem(null);
    setErrors({});
    setForm({
      title: "",
      code: "",
      description: "",
      name: "",
      mobile: "",
      email: "",
      address: "",
      serviceType: "Verve",
      active: true,
      openingDate: "",
      state: "",
      district: "",
      gstRegisteredType: "",
      gstNo: "",
      bankIfsc: "",
      bankName: "",
      bankBranch: "",
      bankAccountHolder: "",
      bankAccountNo: "",
      type: "",
      category: "",
      brand: "",
      itemName: "",
      itemCode: "",
      hsnSacCode: "",
      isUnique: false,
      uniqueName: "",
      isBatchExpiry: false,
      barcode: "",
      units: "",
      itemDescription: "",
    });

    if (activeTab === "purchase") {
      setIsPurchaseFormOpen(true);
    } else {
      setIsModalOpen(true);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setErrors({});

    // Helper to flatten populated objects to ID strings for form selects
    const flatten = (val) =>
      typeof val === "object" && val !== null ? val._id : val || "";

    setForm({
      ...item,
      title: item.title || item.registeredTypeName || item.unitName || item.brandName || item.categoryName || item.storeName || item.name || item.vendorName || "",
      code: item.code || item.registeredTypeCode || item.unitCode || item.brandCode || item.categoryCode || item.storeCode || item.vendorCode || item.itemCode || "",
      name: item.name || item.vendorName || "",
      address: item.address || item.addressLine1 || "",
      mobile: item.mobile || "",
      email: item.email || "",
      state: item.state || "",
      openingDate: item.openingDate || (item.createdDate ? new Date(item.createdDate).toISOString().split('T')[0] : (item.createdAt ? new Date(item.createdAt).toISOString().split('T')[0] : "")),
      district: item.district || item.city || "",
      gstRegisteredType: item.gstRegisteredType || item.gstType || "",
      gstNo: item.gstNo || "",
      bankIfsc: item.bankIfsc || item.ifscCode || "",
      bankName: item.bankName || "",
      bankBranch: item.bankBranch || item.branchName || "",
      bankAccountHolder: item.bankAccountHolder || item.accountHolderName || "",
      bankAccountNo: item.bankAccountNo || item.accountNumber || "",
      category: flatten(item.category),
      brand: flatten(item.brand),
      units: flatten(item.units),
      vendor: flatten(item.vendor),
      active: item.status ? item.status === 'ACTIVE' : (item.active !== undefined ? item.active : true),
    });

    if (activeTab === "purchase") {
      setIsPurchaseFormOpen(true);
    } else {
      setIsModalOpen(true);
    }
  };

  const handleSave = async () => {
    // Enhanced validation
    const validationErrors = validateForm(form, activeTab);
    const errorFields = Object.keys(validationErrors);

    if (errorFields.length > 0) {
      setErrors(validationErrors);
      // Show only the first few errors in toast to avoid clutter
      errorFields
        .slice(0, 3)
        .forEach((field) => toast.error(validationErrors[field]));
      return;
    }

    try {
      const payload = { ...form, settingType: activeTab === 'supplier' ? 'vendor' : activeTab };

      // For items, we use itemCode as the primary identifier, so we sync it with 'code'
      if (activeTab === "item") {
        payload.code = form.itemCode;
      } else if (activeTab === "registeredType") {
        payload.registeredTypeName = form.title;
        payload.registeredTypeCode = form.code;
        payload.status = form.active ? 'ACTIVE' : 'INACTIVE';
      } else if (activeTab === "unit") {
        payload.unitName = form.title;
        payload.unitCode = form.code;
        payload.status = form.active ? 'ACTIVE' : 'INACTIVE';
      } else if (activeTab === "brand") {
        payload.brandName = form.title;
        payload.brandCode = form.code;
        payload.status = form.active ? 'ACTIVE' : 'INACTIVE';
      } else if (activeTab === "category") {
        payload.categoryName = form.title;
        payload.categoryCode = form.code;
        payload.status = form.active ? 'ACTIVE' : 'INACTIVE';
      } else if (activeTab === "stores") {
        payload.storeName = form.title;
        payload.storeCode = form.code;
        payload.addressLine1 = form.address;
        payload.status = form.active ? 'ACTIVE' : 'INACTIVE';
      } else if (activeTab === "supplier") {
        payload.vendorName = form.name;
        payload.vendorCode = form.code;
        payload.addressLine1 = form.address;
        payload.city = form.district;
        payload.gstType = form.gstRegisteredType;
        payload.ifscCode = form.bankIfsc;
        payload.branchName = form.bankBranch;
        payload.accountHolderName = form.bankAccountHolder;
        payload.accountNumber = form.bankAccountNo;
        payload.status = form.active ? 'ACTIVE' : 'INACTIVE';
      }

      if (payload.vendor === "") {
        delete payload.vendor;
      }
      if (editingItem) {
        await services.updateInventorySetting(editingItem._id, payload);
        toast.success("Updated successfully");
      } else {
        await services.createInventorySetting(payload);
        toast.success("Added successfully");
      }
      setIsModalOpen(false);
      setErrors({});
      fetchSettings();
      fetchDropdownData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    }
  };

  // Handle input changes and clear errors
  const handleInputChange = (e) => {
    const field = e.target.name || e.target.id;
    let value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    // --- Real-time Input Restrictions ---

    // 1. Mobile: Only 10 digits
    if (field === "mobile") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }

    // 2. Bank Account Number: Only digits, max 18
    if (field === "bankAccountNo") {
      value = value.replace(/\D/g, "").slice(0, 18);
    }

    // 3. IFSC & GST: Auto-uppercase
    if (field === "bankIfsc" || field === "gstNo") {
      value = value.toUpperCase().replace(/\s/g, "");
    }

    // 4. Numerical fields (Amounts): Only digits/decimal
    const numericFields = [];
    if (numericFields.includes(field)) {
      // Keep only digits and one decimal point
      value = value.replace(/[^0-9.]/g, "");
      const parts = value.split(".");
      if (parts.length > 2) value = parts[0] + "." + parts.slice(1).join("");
    }

    setForm((prev) => ({ ...prev, [field]: value }));

    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this?")) {
      try {
        let res;
        if (activeTab === "purchase") {
          res = await services.deletePurchase(id);
        } else {
          const mappedTab = activeTab === "supplier" ? "vendor" : activeTab;
          res = await services.deleteInventorySetting(id, mappedTab);
        }

        if (res.status === "success") {
          toast.success("Deleted successfully");
          fetchSettings();
        }
      } catch (err) {
        toast.error("Failed to delete");
      }
    }
  };

  const validateForm = (formData, tabType) => {
    const errors = {};

    // Code is now optional for all types
    if (tabType !== "item" && formData.code && formData.code.length > 20) {
      errors.code = "Code should not exceed 20 characters";
    }

    if (tabType === "supplier") {
      const name = (formData.name || "").trim();
      const mobile = (formData.mobile || "").trim();
      const address = (formData.address || "").trim();
      const email = (formData.email || "").trim();
      const bankIfsc = (formData.bankIfsc || "").trim().toUpperCase();
      const bankAccountNo = (formData.bankAccountNo || "").trim();
      const gstNo = (formData.gstNo || "").trim().toUpperCase();

      if (!name || name.length < 2) {
        errors.name = "Name is required and must be at least 2 characters";
      }
      if (!mobile || !/^\d{10}$/.test(mobile.replace(/\D/g, ""))) {
        errors.mobile = "Valid 10-digit mobile number is required";
      }
      if (!address || address.length < 5) {
        errors.address = "Address is required";
      }
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.email = "Please enter a valid email address";
      }
      // New vendor validations
      if (!formData.openingDate) {
        errors.openingDate = "Opening date is required";
      }
      if (!formData.state) {
        errors.state = "State is required";
      }
      if (!formData.district) {
        errors.district = "District is required";
      }
      if (!formData.gstRegisteredType) {
        errors.gstRegisteredType = "GST registered type is required";
      }
      if (
        gstNo &&
        !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gstNo)
      ) {
        errors.gstNo = "Please enter a valid GST number (e.g. 29AAAAA0000A1Z5)";
      }
      // Bank details validations
      if (bankIfsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bankIfsc)) {
        errors.bankIfsc = "Please enter a valid IFSC code (e.g. SBIN0001234)";
      }
      if (bankAccountNo && !/^\d{9,18}$/.test(bankAccountNo)) {
        errors.bankAccountNo =
          "Please enter a valid account number (9-18 digits)";
      }
    } else if (tabType === "registeredType") {
      if (!formData.title || formData.title.trim().length < 2) {
        errors.title =
          "Type name is required and must be at least 2 characters";
      }
      if (formData.description && formData.description.length > 500) {
        errors.description = "Description should not exceed 500 characters";
      }
    } else if (tabType === "item") {
      if (!formData.type) {
        errors.type = "Item type is required";
      }
      if (!formData.category) {
        errors.category = "Category selection is required";
      }
      if (!formData.brand) {
        errors.brand = "Brand selection is required";
      }
      if (!formData.itemName || formData.itemName.trim().length < 2) {
        errors.itemName =
          "Item name is required and must be at least 2 characters";
      }
      // Set name to itemName for backend compatibility
      formData.name = formData.itemName;
      // itemCode is now optional
      if (formData.itemCode && formData.itemCode.length > 20) {
        errors.itemCode = "Item code should not exceed 20 characters";
      }
      if (formData.isUnique && !formData.uniqueName) {
        errors.uniqueName =
          "Unique name is required when item is marked as unique";
      }
      if (!formData.units) {
        errors.units = "Units are required";
      }
    } else if (tabType === "stores") {
      if (!formData.title || formData.title.trim().length < 2) {
        errors.title =
          "Store Name is required and must be at least 2 characters";
      }
      if (formData.code && formData.code.trim().length < 2) {
        errors.code = "Store Code must be at least 2 characters if provided";
      }
      if (!formData.address || formData.address.trim().length < 5) {
        errors.address = "Store Address is required (min 5 characters)";
      }
      if (
        formData.mobile &&
        !/^\d{10}$/.test(formData.mobile.replace(/\D/g, ""))
      ) {
        errors.mobile = "If provided, mobile must be a valid 10-digit number";
      }
    } else {
      // Original validations for category, unit, brand
      if (!formData.title || formData.title.trim().length < 2) {
        errors.title = "Title is required and must be at least 2 characters";
      }
      if (formData.description && formData.description.length > 500) {
        errors.description = "Description should not exceed 500 characters";
      }
    }

    return errors;
  };

  const getFilteredData = () => {
    const searchStr = search.toLowerCase();
    return data.filter((item) => {
      if (activeTab === "supplier") {
        const vendorName = item.name || item.vendorName || "";
        const vendorCode = item.code || item.vendorCode || "";
        return (
          vendorName.toLowerCase().includes(searchStr) ||
          (item.mobile || "").includes(searchStr) ||
          vendorCode.toLowerCase().includes(searchStr)
        );
      } else if (activeTab === "registeredType") {
        const itemTitle = item.title || item.registeredTypeName || "";
        const itemCode = item.code || item.registeredTypeCode || "";
        return (
          itemTitle.toLowerCase().includes(searchStr) ||
          itemCode.toLowerCase().includes(searchStr) ||
          (item.description?.toLowerCase() || "").includes(searchStr)
        );
      } else if (activeTab === "item") {
        const categoryStr = item.category?.title || "";
        const brandStr = item.brand?.title || "";
        return (
          (item.itemName?.toLowerCase() || "").includes(searchStr) ||
          (item.itemCode?.toLowerCase() || "").includes(searchStr) ||
          categoryStr.toLowerCase().includes(searchStr) ||
          brandStr.toLowerCase().includes(searchStr) ||
          (item.createdAt || item.createdDate || item.created
            ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString().includes(searchStr)
            : false)
        );
      } else if (activeTab === "stores") {
        const storeTitle = item.title || item.storeName || "";
        const storeCode = item.code || item.storeCode || "";
        return (
          storeTitle.toLowerCase().includes(searchStr) ||
          storeCode.toLowerCase().includes(searchStr) ||
          (item.address?.toLowerCase() || "").includes(searchStr) ||
          (item.mobile || "").includes(searchStr) ||
          (item.createdAt || item.createdDate || item.created
            ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString().includes(searchStr)
            : false)
        );
      } else if (activeTab === "purchase") {
        const vendorStr =
          typeof item.vendor === "object"
            ? item.vendor?.name || item.vendor?.title || ""
            : "";
        return (
          (item.billNo?.toLowerCase() || "").includes(searchStr) ||
          vendorStr.toLowerCase().includes(searchStr)
        );
      }
      
      const itemTitle = item.title || item.unitName || item.brandName || item.categoryName || "";
      const itemCode = item.code || item.unitCode || item.brandCode || item.categoryCode || "";
      return (
        itemTitle.toLowerCase().includes(searchStr) ||
        itemCode.toLowerCase().includes(searchStr) ||
        (item.createdAt || item.createdDate || item.created
          ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString().includes(searchStr)
          : false)
      );
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-brand-dark">
          
        </h1>
        <button
          onClick={handleAdd}
          className="btn-primary flex items-center justify-center gap-2 py-2.5 px-6 shadow-lg shadow-blue-900/20 active:scale-95 transition-all"
        >
          <Plus size={18} />{" "}
          <span> {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</span>
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="flex border-b border-slate-100 bg-slate-50/50 overflow-x-auto no-scrollbar whitespace-nowrap px-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setActiveTab(t.id);
                setSearch("");
              }}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-all ${activeTab === t.id ? "border-brand-blue text-brand-blue bg-white" : "border-transparent text-slate-500 hover:text-brand-dark hover:bg-slate-50"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder={`Search by title or code...`}
            className="w-full sm:max-w-md"
          />
        </div>

        <div className="overflow-x-auto min-h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-brand-blue border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : activeTab === "supplier" ? (
            <Table
              headers={[
                "Code",
                "Name",
                "Contact",
                "Address",
                "Created At",
                "Status",
                "Actions",
              ]}
            >
              {getFilteredData().map((s) => (
                <tr
                  key={s._id}
                  className="hover:bg-slate-50 transition-colors align-top h-[34px]"
                >
                  <td className="px-4 py-1.5 text-[12px] font-mono font-bold text-brand-blue text-left">
                    {s.code || s.vendorCode || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[13px] font-semibold text-brand-dark text-left">
                    {s.name || s.vendorName}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {s.mobile}
                  </td>
                  <td
                    className="px-4 py-1.5 text-[12px] text-slate-500 font-medium truncate max-w-[200px] text-left"
                    title={s.address || s.addressLine1}
                  >
                    {s.address || s.addressLine1 || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[11px] font-mono font-bold text-slate-400 text-left">
                    {(s.createdAt || s.createdDate || s.created)
                      ? new Date(s.createdAt || s.createdDate || s.created).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    {s.active !== false ? (
                      <span className="inline-flex gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100">
                        <CheckCircle2 size={10} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-slate-50 text-slate-400 border border-slate-200">
                        <XCircle size={10} /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    <div className="flex gap-1">
                      <button
                        onClick={() => setViewingItem(s)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleEdit(s)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(s._id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          ) : activeTab === "registeredType" ? (
            <Table
              headers={[
                "Code",
                "Name",
                "Description",
                "Created At",
                "Status",
                "Actions",
              ]}
            >
              {getFilteredData().map((item) => (
                <tr
                  key={item._id}
                  className="hover:bg-slate-50 transition-colors align-top h-[34px]"
                >
                  <td className="px-4 py-1.5 text-[12px] font-mono font-bold text-brand-blue text-left">
                    {item.code || item.registeredTypeCode || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[13px] font-semibold text-brand-dark text-left">
                    {item.title || item.registeredTypeName || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] text-slate-600 font-medium text-left">
                    {item.description || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[11px] font-mono font-bold text-slate-400 text-left">
                    {(item.createdAt || item.createdDate || item.created)
                      ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    {(item.active !== false && item.status !== 'INACTIVE') ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100">
                        <CheckCircle2 size={10} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-slate-50 text-slate-400 border border-slate-200">
                        <XCircle size={10} /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setViewingItem(item)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          ) : activeTab === "purchase" ? (
            <Table
              headers={[
                "Bill No",
                "Vendor",
                "Total Amount",
                "Bill Date",
                "Created At",
                "Actions",
              ]}
            >
              {getFilteredData().map((p) => (
                <tr
                  key={p._id}
                  className="hover:bg-slate-50 transition-colors align-top h-[34px]"
                >
                  <td className="px-4 py-1.5 text-[12px] font-mono font-bold text-brand-blue text-left">
                    {p.billNo || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[13px] font-semibold text-brand-dark text-left">
                    {typeof p.vendor === "object"
                      ? p.vendor?.name || p.vendor?.title
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-bold text-emerald-600 text-left">
                    {fmt(p.netPayable || 0)}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {p.date ? new Date(p.date).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[11px] font-mono font-bold text-slate-400 text-left">
                    {(p.createdAt || p.createdDate || p.created)
                      ? new Date(p.createdAt || p.createdDate || p.created).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setViewingItem(p)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleEdit(p)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(p._id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          ) : activeTab === "item" ? (
            <Table
              headers={[
                "Code",
                "Name",
                "Category",
                "Brand",
                "Units",
                "Created At",
                "Actions",
              ]}
            >
              {getFilteredData().map((item) => (
                <tr
                  key={item._id}
                  className="hover:bg-slate-50 transition-colors align-top h-[34px]"
                >
                  <td className="px-4 py-1.5 text-[12px] font-mono font-bold text-brand-blue text-left">
                    {item.itemCode || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[13px] font-semibold text-brand-dark text-left">
                    {item.itemName}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {item.category?.categoryName || item.category?.title || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {item.brand?.brandName || item.brand?.title || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {item.units?.unitName || item.units?.title || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[11px] font-mono font-bold text-slate-400 text-left">
                    {(item.createdAt || item.createdDate || item.created)
                      ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setViewingItem(item)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          ) : activeTab === "stores" ? (
            <Table
              headers={[
                "Code",
                "Name",
                "Address",
                "Contact",
                "Created At",
                "Status",
                "Actions",
              ]}
            >
              {getFilteredData().map((store) => (
                <tr
                  key={store._id}
                  className="hover:bg-slate-50 transition-colors align-top h-[34px]"
                >
                  <td className="px-4 py-1.5 text-[12px] font-mono font-bold text-brand-blue text-left">
                    {store.code || store.storeCode || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[13px] font-semibold text-brand-dark text-left">
                    {store.title || store.storeName}
                  </td>
                  <td
                    className="px-4 py-1.5 text-[12px] text-slate-500 font-medium truncate max-w-[200px] text-left"
                    title={store.address || store.addressLine1}
                  >
                    {store.address || store.addressLine1 || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {store.mobile || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[11px] font-mono font-bold text-slate-400 text-left">
                    {(store.createdAt || store.createdDate || store.created)
                      ? new Date(store.createdAt || store.createdDate || store.created).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    {store.active !== false ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100">
                        <CheckCircle2 size={10} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-slate-50 text-slate-400 border border-slate-200">
                        <XCircle size={10} /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setViewingItem(store)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleEdit(store)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(store._id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          ) : (
            <Table
              headers={[
                "Code",
                "Title",
                "Description",
                "Created At",
                "Status",
                "Actions",
              ]}
            >
              {getFilteredData().map((item) => (
                <tr
                  key={item._id}
                  className="hover:bg-slate-50 transition-colors align-top h-[34px]"
                >
                  <td className="px-4 py-1.5 text-[12px] font-mono font-bold text-brand-blue text-left">
                    {item.code || item.unitCode || item.brandCode || item.categoryCode || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[13px] font-semibold text-brand-dark text-left">
                    {item.title || item.unitName || item.brandName || item.categoryName || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] text-slate-600 font-medium text-left">
                    {item.description || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[11px] font-mono font-bold text-slate-400 text-left">
                    {(item.createdAt || item.createdDate || item.created)
                      ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    {item.active !== false ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100">
                        <CheckCircle2 size={10} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-slate-50 text-slate-400 border border-slate-200">
                        <XCircle size={10} /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setViewingItem(item)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`${editingItem ? "Edit" : "Add"} ${
          activeTab === "registeredType" ? "Registered Type" : 
          activeTab === "gstRegisteredType" ? "GST Registered Type" : 
          activeTab.charAt(0).toUpperCase() + activeTab.slice(1)
        }`}
      >
        <div className="space-y-6">
          {activeTab === "supplier" ? (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300 pb-10 sm:pb-0">
              {/* Section: Core Information */}
              <div className="space-y-4">
                <h3 className="text-[11px] font-black text-brand-blue uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-2">
                  <User size={14} /> Core Information
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
                  <FormInput
                    icon={<User size={14} />}
                    label="Company Name"
                    required
                    placeholder="Vendor name"
                    field="name"
                    value={form.name}
                    onChange={handleInputChange}
                    error={errors.name}
                  />
                  <FormInput
                    icon={<Hash size={14} />}
                    label="Vendor Code"
                    placeholder="e.g. SUP-001 "
                    field="code"
                    value={form.code}
                    onChange={handleInputChange}
                    error={errors.code}
                  />
                  <FormInput
                    icon={<Phone size={14} />}
                    label="Mobile"
                    required
                    placeholder="10-digit number"
                    field="mobile"
                    value={form.mobile}
                    onChange={handleInputChange}
                    error={errors.mobile}
                  />
                  <FormInput
                    icon={<Mail size={14} />}
                    label="Email"
                    placeholder="Email address"
                    field="email"
                    type="email"
                    value={form.email}
                    onChange={handleInputChange}
                    error={errors.email}
                  />
                  <div className="lg:col-span-2">
                    <FormTextarea
                      icon={<MapPin size={14} />}
                      label="Address"
                      required
                      placeholder="Full address details..."
                      field="address"
                      value={form.address}
                      onChange={handleInputChange}
                      error={errors.address}
                    />
                  </div>
                  <FormInput
                    icon={<Calendar size={14} />}
                    label="Opening Date"
                    required
                    field="openingDate"
                    type="date"
                    value={form.openingDate}
                    onChange={handleInputChange}
                    error={errors.openingDate}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <FormInput
                      icon={<MapPin size={14} />}
                      label="State"
                      required
                      placeholder="State"
                      field="state"
                      value={form.state}
                      onChange={handleInputChange}
                      error={errors.state}
                    />
                    <FormInput
                      icon={<MapPin size={14} />}
                      label="District"
                      required
                      placeholder="District"
                      field="district"
                      value={form.district}
                      onChange={handleInputChange}
                      error={errors.district}
                    />
                  </div>
                </div>
              </div>

              {/* Section: Business & GST Information */}
              <div className="space-y-4 pt-4">
                <h3 className="text-[11px] font-black text-brand-blue uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-2">
                  <Building size={14} /> Business & GST Information
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
                  <FormSelect
                    icon={<Building size={14} />}
                    label="GST Registered Type"
                    required
                    placeholder="Select Registered Type"
                    field="gstRegisteredType"
                    options={gstRegisteredTypes}
                    value={form.gstRegisteredType}
                    onChange={handleInputChange}
                    error={errors.gstRegisteredType}
                  />
                  <FormInput
                    icon={<Building size={14} />}
                    label="GST Number"
                    placeholder="e.g. 29AAAAA0000A1Z5"
                    field="gstNo"
                    value={form.gstNo}
                    onChange={handleInputChange}
                    error={errors.gstNo}
                  />
                </div>
              </div>

              {/* Section: Bank Information */}
              <div className="space-y-4 pt-4">
                <h3 className="text-[11px] font-black text-brand-blue uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-2">
                  <CreditCard size={14} /> Bank Account Information
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
                  <FormInput
                    icon={<CreditCard size={14} />}
                    label="Bank Name"
                    placeholder="e.g. State Bank of India"
                    field="bankName"
                    value={form.bankName}
                    onChange={handleInputChange}
                    error={errors.bankName}
                  />
                  <FormInput
                    icon={<CreditCard size={14} />}
                    label="IFSC Code"
                    placeholder="e.g. SBIN0001234"
                    field="bankIfsc"
                    value={form.bankIfsc}
                    onChange={handleInputChange}
                    error={errors.bankIfsc}
                  />
                  <FormInput
                    icon={<Building size={14} />}
                    label="Branch"
                    placeholder="e.g. Main Branch"
                    field="bankBranch"
                    value={form.bankBranch}
                    onChange={handleInputChange}
                    error={errors.bankBranch}
                  />
                  <FormInput
                    icon={<User size={14} />}
                    label="Account Holder"
                    placeholder="e.g. John Doe"
                    field="bankAccountHolder"
                    value={form.bankAccountHolder}
                    onChange={handleInputChange}
                    error={errors.bankAccountHolder}
                  />
                  <FormInput
                    icon={<CreditCard size={14} />}
                    label="Account Number"
                    placeholder="e.g. 100021345678"
                    field="bankAccountNo"
                    value={form.bankAccountNo}
                    onChange={handleInputChange}
                    error={errors.bankAccountNo}
                  />
                  <div className="flex flex-col justify-end">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 opacity-80">
                      Status
                    </label>
                    <select
                      className={`input h-[44px] font-bold text-xs uppercase tracking-wider bg-slate-50/50 border-slate-200 focus:bg-white transition-all cursor-pointer ${form.active ? "text-emerald-600" : "text-slate-400"}`}
                      value={form.active ? "active" : "inactive"}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          active: e.target.value === "active",
                        })
                      }
                    >
                      <option
                        value="active"
                        className="text-emerald-600 font-bold"
                      >
                        Active
                      </option>
                      <option
                        value="inactive"
                        className="text-slate-400 font-bold"
                      >
                        Inactive
                      </option>
                    </select>
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <FormTextarea
                    icon={<Package size={14} />}
                    label="Description"
                    placeholder="Type description..."
                    field="description"
                    value={form.description}
                    onChange={handleInputChange}
                    error={errors.description}
                  />
                </div>
              </div>
            </div>
          ) : activeTab === "registeredType" ? (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
                <FormInput
                  icon={<Hash size={14} />}
                  label="Type Name"
                  required
                  placeholder="e.g. Regular, Composite"
                  field="title"
                  value={form.title}
                  onChange={handleInputChange}
                  error={errors.title}
                />
                
                <FormInput
                  icon={<Hash size={14} />}
                  label="Code"
                  placeholder="e.g. REG, COMP"
                  field="code"
                  value={form.code}
                  onChange={handleInputChange}
                  error={errors.code}
                />

                <div className="flex flex-col justify-end">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 opacity-80">
                    Status
                  </label>
                  <select
                    className={`input h-[44px] font-bold text-xs uppercase tracking-wider bg-slate-50/50 border-slate-200 focus:bg-white transition-all cursor-pointer ${form.active ? "text-emerald-600" : "text-slate-400"}`}
                    value={form.active ? "active" : "inactive"}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        active: e.target.value === "active",
                      })
                    }
                  >
                    <option value="active" className="text-emerald-600 font-bold">
                      Active
                    </option>
                    <option value="inactive" className="text-slate-400 font-bold">
                      Inactive
                    </option>
                  </select>
                </div>

                <FormTextarea
                  icon={<Package size={14} />}
                  label="Description"
                  placeholder="Type description..."
                  field="description"
                  value={form.description}
                  onChange={handleInputChange}
                  error={errors.description}
                />
              </div>
            </div>
          ) : activeTab === "stores" ? (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
                <FormInput
                  icon={<Building size={14} />}
                  label="Store Name"
                  required
                  placeholder="e.g. Main Warehouse"
                  field="title"
                  value={form.title}
                  onChange={handleInputChange}
                  error={errors.title}
                />
                <FormInput
                  icon={<Hash size={14} />}
                  label="Store Code"
                  placeholder="e.g. STR-001"
                  field="code"
                  value={form.code}
                  onChange={handleInputChange}
                  error={errors.code}
                />
                <FormInput
                  icon={<Phone size={14} />}
                  label="Contact Number"
                  placeholder="10-digit mobile"
                  field="mobile"
                  value={form.mobile}
                  onChange={handleInputChange}
                  error={errors.mobile}
                />
                <div className="flex flex-col justify-end">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 opacity-80">
                    Status
                  </label>
                  <select
                    className={`input h-[44px] font-bold text-xs uppercase tracking-wider bg-slate-50/50 border-slate-200 focus:bg-white transition-all cursor-pointer ${form.active ? "text-emerald-600" : "text-slate-400"}`}
                    value={form.active ? "active" : "inactive"}
                    onChange={(e) =>
                      setForm({ ...form, active: e.target.value === "active" })
                    }
                  >
                    <option
                      value="active"
                      className="text-emerald-600 font-bold"
                    >
                      Active
                    </option>
                    <option
                      value="inactive"
                      className="text-slate-400 font-bold"
                    >
                      Inactive
                    </option>
                  </select>
                </div>
                <div className="lg:col-span-2">
                  <FormTextarea
                    icon={<MapPin size={14} />}
                    label="Store Address"
                    required
                    placeholder="Full address of the store..."
                    field="address"
                    value={form.address}
                    onChange={handleInputChange}
                    error={errors.address}
                  />
                </div>
                <div className="lg:col-span-2">
                  <FormTextarea
                    icon={<Package size={14} />}
                    label="Description"
                    placeholder="Type description..."
                    field="description"
                    value={form.description}
                    onChange={handleInputChange}
                    error={errors.description}
                  />
                </div>
              </div>
            </div>
          ) : activeTab === "item" ? (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
                {/* Row 1 */}
                <FormSelect
                  icon={<Package size={14} />}
                  label="Item Type"
                  required
                  placeholder="Select Type"
                  field="type"
                  options={[
                    { value: "product", label: "Product" },
                    { value: "service", label: "Service" },
                    { value: "material", label: "Material" },
                  ]}
                  value={form.type}
                  onChange={handleInputChange}
                  error={errors.type}
                />
                <FormSelect
                  icon={<Package size={14} />}
                  label="Units"
                  required
                  placeholder="Select Unit"
                  field="units"
                  options={units}
                  value={form.units}
                  onChange={handleInputChange}
                  error={errors.units}
                />

                {/* Row 2 */}
                <FormSelect
                  icon={<Package size={14} />}
                  label="Category"
                  required
                  placeholder="Select Category"
                  field="category"
                  options={categories}
                  value={form.category}
                  onChange={handleInputChange}
                  error={errors.category}
                />
                {/* Row 3 */}
                <FormSelect
                  icon={<Building size={14} />}
                  label="Brand"
                  required
                  placeholder="Select Brand"
                  field="brand"
                  options={brands}
                  value={form.brand}
                  onChange={handleInputChange}
                  error={errors.brand}
                />
                <FormInput
                  icon={<Package size={14} />}
                  label="Item Name"
                  required
                  placeholder="Item Name"
                  field="itemName"
                  value={form.itemName}
                  onChange={handleInputChange}
                  error={errors.itemName}
                />

                {/* Row 4 */}
                <FormInput
                  icon={<Hash size={14} />}
                  label="Item Code"
                  placeholder="Item Code"
                  field="itemCode"
                  value={form.itemCode}
                  onChange={handleInputChange}
                  error={errors.itemCode}
                  required
                />

                {/* Row 6 */}
                <FormInput
                  icon={<Hash size={14} />}
                  label="HSN/SAC Code"
                  placeholder="HSN/SAC Code"
                  field="hsnSacCode"
                  value={form.hsnSacCode}
                  onChange={handleInputChange}
                  error={errors.hsnSacCode}
                />
                <div className="space-y-4 pt-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="isUnique"
                      name="isUnique"
                      className="w-4 h-4 text-brand-blue border-slate-300 rounded focus:ring-brand-blue transition-all cursor-pointer"
                      checked={form.isUnique || false}
                      onChange={handleInputChange}
                    />
                    <label
                      htmlFor="isUnique"
                      className="text-xs font-bold text-slate-500 uppercase tracking-widest cursor-pointer"
                    >
                      Is Unique Item
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="isBatchExpiry"
                      name="isBatchExpiry"
                      className="w-4 h-4 text-brand-blue border-slate-300 rounded focus:ring-brand-blue transition-all cursor-pointer"
                      checked={form.isBatchExpiry || false}
                      onChange={handleInputChange}
                    />
                    <label
                      htmlFor="isBatchExpiry"
                      className="text-xs font-bold text-slate-500 uppercase tracking-widest cursor-pointer"
                    >
                      Has Batch/Expiry
                    </label>
                  </div>
                </div>

                {/* Row 7 */}
                <FormInput
                  icon={<Hash size={14} />}
                  label="Barcode"
                  placeholder="Barcode"
                  field="barcode"
                  value={form.barcode}
                  onChange={handleInputChange}
                  error={errors.barcode}
                />
                {form.isUnique && (
                  <FormInput
                    icon={<Hash size={14} />}
                    label="Unique Name"
                    required
                    placeholder="Unique identifier"
                    field="uniqueName"
                    value={form.uniqueName}
                    onChange={handleInputChange}
                    error={errors.uniqueName}
                  />
                )}
              </div>

              {/* Status field before description */}
              <div className="border-t border-slate-100 pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-1 gap-5">
                  <div className="sm:col-span-1">
                    <label className="label text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5 block">
                      Status
                    </label>
                    <select
                      className={`input h-[44px] font-bold text-xs uppercase tracking-wider bg-slate-50/50 border-slate-200 focus:bg-white transition-all cursor-pointer ${form.active ? "text-emerald-600" : "text-slate-400"}`}
                      value={form.active ? "active" : "inactive"}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          active: e.target.value === "active",
                        })
                      }
                    >
                      <option
                        value="active"
                        className="text-emerald-600 font-bold"
                      >
                        Active
                      </option>
                      <option
                        value="inactive"
                        className="text-slate-400 font-bold"
                      >
                        Inactive
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <FormTextarea
                icon={<Package size={14} />}
                label="Description"
                placeholder="Item description..."
                field="itemDescription"
                value={form.itemDescription}
                onChange={handleInputChange}
                error={errors.itemDescription}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <FormInput
                    icon={<Hash size={14} />}
                    label="Title"
                    required
                    placeholder={`e.g. ${activeTab === "category" ? "Solar Panels" : activeTab === "unit" ? "Watts (W)" : "Generic"}`}
                    field="title"
                    value={form.title}
                    onChange={handleInputChange}
                    error={errors.title}
                  />
                </div>

                <div className="space-y-4">
                  <FormInput
                    icon={<Hash size={14} />}
                    label={`${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Code`}
                    placeholder={`e.g. ${activeTab.slice(0, 3).toUpperCase()}-CODE`}
                    field="code"
                    value={form.code}
                    onChange={handleInputChange}
                    error={errors.code}
                  />
                </div>
              </div>

              {/* Status field before description */}
              <div className="border-t border-slate-100 pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-1">
                    <label className="label text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5 block">
                      Status
                    </label>
                    <select
                      className={`input h-[44px] font-bold text-xs uppercase tracking-wider bg-slate-50/50 border-slate-200 focus:bg-white transition-all cursor-pointer ${form.active ? "text-emerald-600" : "text-slate-400"}`}
                      value={form.active ? "active" : "inactive"}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          active: e.target.value === "active",
                        })
                      }
                    >
                      <option
                        value="active"
                        className="text-emerald-600 font-bold"
                      >
                        Active
                      </option>
                      <option
                        value="inactive"
                        className="text-slate-400 font-bold"
                      >
                        Inactive
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <FormTextarea
                icon={<Package size={14} />}
                label="Description"
                placeholder="Additional optional details..."
                field="description"
                value={form.description}
                onChange={handleInputChange}
                error={errors.description}
              />
            </div>
          )}

          <div className="flex flex-col sm:flex-row-reverse gap-3 pt-6 border-t border-slate-100">
            <button
              onClick={handleSave}
              className="btn-primary flex-1 py-3 font-bold shadow-lg shadow-blue-900/20 transition-all active:scale-[0.98] hover:translate-y-[-1px]"
            >
              Save Changes
            </button>
            <button
              onClick={() => setIsModalOpen(false)}
              className="btn-secondary flex-1 py-3 font-semibold transition-all hover:bg-slate-100"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={!!viewingItem}
        onClose={() => setViewingItem(null)}
        title={`View ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Details`}
      >
        {viewingItem && (
          <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
            {/* Header Identity Section */}
            <div className="flex items-center gap-4 pb-4 border-b border-slate-100 mb-2">
              <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center text-brand-blue border border-slate-100 shrink-0">
                {activeTab === "item" ? (
                  <Package size={24} />
                ) : activeTab === "supplier" ? (
                  <User size={24} />
                ) : activeTab === "stores" ? (
                  <Building size={24} />
                ) : (
                  <Hash size={24} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-semibold text-brand-dark truncate">
                    {viewingItem.itemName ||
                      viewingItem.name ||
                      viewingItem.title}
                  </h3>
                  <span className="text-[10px] font-bold bg-blue-50 text-brand-blue px-2 py-0.5 rounded border border-blue-100 shrink-0 uppercase">
                    {viewingItem.itemCode || viewingItem.code || "REF-ID-0"}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-3">
                  {viewingItem.active !== false ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{" "}
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />{" "}
                      Inactive
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Detail Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {activeTab === "item" ? (
                <>
                  <DetailField
                    label="Item Name"
                    value={viewingItem.itemName}
                    fullWidth
                  />
                  <DetailField
                    label="Category"
                    value={
                      viewingItem.category?.title ||
                      viewingItem.category?.name ||
                      "N/A"
                    }
                  />
                  <DetailField
                    label="Brand"
                    value={
                      viewingItem.brand?.title ||
                      viewingItem.brand?.name ||
                      "N/A"
                    }
                  />
                  <DetailField
                    label="Item Type"
                    value={viewingItem.type}
                    className="capitalize"
                  />
                  <DetailField
                    label="Unit"
                    value={
                      viewingItem.units?.title ||
                      viewingItem.units?.name ||
                      "N/A"
                    }
                  />
                  <DetailField
                    label="HSN/SAC Code"
                    value={viewingItem.hsnSacCode}
                  />
                  <DetailField label="Barcode" value={viewingItem.barcode} />
                  <div className="sm:col-span-2 grid grid-cols-2 gap-3 pt-1">
                    <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between transition-all duration-300">
                      <div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider opacity-70 mb-0.5">
                          Unique Item
                        </div>
                        <div className="text-xs font-semibold text-brand-dark">
                          {viewingItem.isUnique ? "Enabled" : "Disabled"}
                        </div>
                      </div>
                      {viewingItem.isUnique ? (
                        <CheckCircle2 size={14} className="text-emerald-500" />
                      ) : (
                        <XCircle size={14} className="text-slate-300" />
                      )}
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between transition-all duration-300">
                      <div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider opacity-70 mb-0.5">
                          Batch/Expiry
                        </div>
                        <div className="text-xs font-semibold text-brand-dark">
                          {viewingItem.isBatchExpiry ? "Enabled" : "Disabled"}
                        </div>
                      </div>
                      {viewingItem.isBatchExpiry ? (
                        <CheckCircle2 size={14} className="text-emerald-500" />
                      ) : (
                        <XCircle size={14} className="text-slate-300" />
                      )}
                    </div>
                  </div>
                  {viewingItem.isUnique && viewingItem.uniqueName && (
                    <DetailField
                      label="Unique Identifier"
                      value={viewingItem.uniqueName}
                      fullWidth
                    />
                  )}
                  <DetailField
                    label="Description"
                    value={viewingItem.itemDescription}
                    fullWidth
                  />
                  <DetailField
                    label="Created On"
                    value={
                      (viewingItem.createdAt || viewingItem.createdDate || viewingItem.created)
                        ? new Date(viewingItem.createdAt || viewingItem.createdDate || viewingItem.created).toLocaleString()
                        : "N/A"
                    }
                    fullWidth
                  />
                </>
              ) : activeTab === "supplier" ? (
                <>
                  <DetailField
                    label="Company Name"
                    value={viewingItem.name}
                    fullWidth
                  />
                  <DetailField
                    label="Mobile Number"
                    value={viewingItem.mobile}
                  />
                  <DetailField
                    label="Email Address"
                    value={viewingItem.email}
                  />
                  <DetailField
                    label="GST Registration"
                    value={
                      viewingItem.gstRegisteredType?.title ||
                      viewingItem.gstNo ||
                      "N/A"
                    }
                  />
                  <DetailField label="GST Number" value={viewingItem.gstNo} />
                  <DetailField
                    label="Address"
                    value={`${viewingItem.address}, ${viewingItem.district}, ${viewingItem.state}`}
                    fullWidth
                  />
                  <DetailField
                    label="Created On"
                    value={
                      (viewingItem.createdAt || viewingItem.createdDate || viewingItem.created)
                        ? new Date(viewingItem.createdAt || viewingItem.createdDate || viewingItem.created).toLocaleString()
                        : "N/A"
                    }
                    fullWidth
                  />
                  <div className="sm:col-span-2 pt-2">
                    <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 px-1">
                      Bank Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <DetailField
                        label="Bank Name"
                        value={viewingItem.bankName}
                      />
                      <DetailField
                        label="IFSC Code"
                        value={viewingItem.bankIfsc}
                      />
                      <DetailField
                        label="Account Holder"
                        value={viewingItem.bankAccountHolder}
                      />
                      <DetailField
                        label="Account Number"
                        value={viewingItem.bankAccountNo}
                      />
                    </div>
                  </div>
                </>
              ) : activeTab === "stores" ? (
                <>
                  <DetailField
                    label="Store Name"
                    value={viewingItem.title}
                    fullWidth
                  />
                  <DetailField label="Store Code" value={viewingItem.code} />
                  <DetailField
                    label="Contact Number"
                    value={viewingItem.mobile || "N/A"}
                  />
                  <DetailField
                    label="Full Address"
                    value={viewingItem.address}
                    fullWidth
                  />
                  <DetailField
                    label="Created On"
                    value={
                      (viewingItem.createdAt || viewingItem.createdDate || viewingItem.created)
                        ? new Date(viewingItem.createdAt || viewingItem.createdDate || viewingItem.created).toLocaleString()
                        : "N/A"
                    }
                    fullWidth
                  />
                </>
              ) : activeTab === "purchase" ? (
                <>
                  <DetailField label="Bill No" value={viewingItem.billNo} />
                  <DetailField
                    label="Vendor"
                    value={
                      typeof viewingItem.vendor === "object"
                        ? viewingItem.vendor?.name
                        : viewingItem.vendor
                    }
                  />
                  <DetailField
                    label="Bill Date"
                    value={
                      viewingItem.date
                        ? new Date(viewingItem.date).toLocaleDateString()
                        : "N/A"
                    }
                  />
                  <DetailField
                    label="Net Payable"
                    value={fmt(viewingItem.netPayable || 0)}
                    className="text-emerald-600"
                  />
                  <DetailField label="Buyer" value={viewingItem.buyer} />
                  <DetailField
                    label="Store"
                    value={
                      typeof viewingItem.store === "object"
                        ? viewingItem.store?.title
                        : viewingItem.store
                    }
                  />

                  <div className="sm:col-span-2 pt-4">
                    <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3 border-b border-slate-100 pb-1">
                      Purchased Items
                    </h4>
                    <div className="space-y-2">
                      {viewingItem.items?.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-xs"
                        >
                          <div className="flex-1">
                            <span className="font-bold text-brand-dark">
                              {typeof item.item === "object"
                                ? item.item?.itemName || item.item?.name
                                : "Item"}
                            </span>
                            <span className="ml-2 text-slate-400">
                              × {item.qty} {item.unit}
                            </span>
                          </div>
                          <div className="font-bold text-slate-700">
                            {fmt(item.amount + (item.taxAmount || 0))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <DetailField
                    label="Notes"
                    value={viewingItem.remarks}
                    fullWidth
                  />
                  <DetailField
                    label="Created On"
                    value={
                      (viewingItem.createdAt || viewingItem.createdDate || viewingItem.created)
                        ? new Date(viewingItem.createdAt || viewingItem.createdDate || viewingItem.created).toLocaleString()
                        : "N/A"
                    }
                    fullWidth
                  />
                </>
              ) : (
                <>
                  <DetailField
                    label="Name / Title"
                    value={viewingItem.title || viewingItem.name || viewingItem.registeredTypeName}
                    fullWidth
                  />
                  <DetailField
                    label="Description"
                    value={viewingItem.description}
                    fullWidth
                  />
                  <DetailField
                    label="Created On"
                    value={
                      (viewingItem.createdAt || viewingItem.createdDate || viewingItem.created)
                        ? new Date(viewingItem.createdAt || viewingItem.createdDate || viewingItem.created).toLocaleString()
                        : "N/A"
                    }
                    fullWidth
                  />
                </>
              )}
            </div>

            <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  setViewingItem(null);
                  handleEdit(viewingItem);
                }}
                className="btn-primary px-8 py-2 font-black text-[10px] uppercase tracking-widest flex items-center gap-2 hover:scale-[1.02] active:scale-95 transition-all shadow-md shadow-blue-500/10"
              >
                <Edit size={12} /> Edit Details
              </button>
              <button
                onClick={() => setViewingItem(null)}
                className="btn-secondary px-8 py-2 font-black text-[10px] uppercase tracking-widest hover:bg-slate-100 transition-all border-slate-200"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Purchase Form Modal */}
      {/* <PurchaseForm 
                isOpen={isPurchaseFormOpen}
                onClose={() => setIsPurchaseFormOpen(false)}
                user={user}
                onPurchaseSuccess={fetchSettings}
                initialData={editingItem}
                dynamicSettings={{
                    stores: stores,
                    suppliers: vendors
                }}
            /> */}
    </div>
  );
};
export default InventorySettings;
